import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { openExternalLink } from '../utils/openExternalLink';
import { getSessionId, getDevice, trackProductView, trackMetaViewContent } from '../utils/analytics';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeart, 
  faLink, 
  faChevronLeft, 
  faChevronRight,
  faSpinner,
  faCircleCheck,
  faTruckFast,
  faShieldHalved,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import ProductCard from '../JSComponents/ProductCard';
import { isSizeInStock, collectDisplaySizes } from '../utils/sortSizes';
import '../StylesComponents/product-page.css';

// Simple Price History Sparkline
const PriceHistoryChart = ({ history, isPro }) => {
  if (!isPro) {
    return (
      <div className="history-locked-overlay">
        <FontAwesomeIcon icon={faLock} className="lock-icon" />
        <span>MARKET HISTORY LOCKED</span>
        <Link to="/pro" className="unlock-link">UPGRADE_TO_UNLOCK</Link>
      </div>
    );
  }

  if (!history || history.length < 2) {
    return <div className="no-history-data">INSUFFICIENT DATA FOR SIGNAL ANALYSIS</div>;
  }

  const prices = history.map(h => h.price);
  const max = Math.max(...prices);
  const min = Math.min(...prices);
  const range = max - min || 1;
  const width = 1000;
  const height = 200;

  const points = history.map((h, i) => {
    const x = (i / (history.length - 1)) * width;
    const y = height - ((h.price - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="sparkline-container">
      <svg viewBox={`0 0 ${width} ${height}`} className="price-sparkline">
        <polyline
          fill="none"
          stroke="var(--color-savings)"
          strokeWidth="4"
          points={points}
        />
      </svg>
      <div className="sparkline-labels">
        <span className="min-label">LOW: ${min.toFixed(2)}</span>
        <span className="max-label">HIGH: ${max.toFixed(2)}</span>
      </div>
    </div>
  );
};

function ProductPage({ currentUser }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [offers, setOffers] = useState([]);
  const [activeOffer, setActiveOffer] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [retailerProducts, setRetailerProducts] = useState([]);
  const [loadingRetailerProducts, setLoadingRetailerProducts] = useState(false);
  const { convertPrice, getCurrencySymbol } = useCurrency();
  const { toggleWishlist, isInWishlist } = useWishlist();

  const isPro = currentUser?.subscription?.status === 'pro';

  const cleanProductName = (name) => {
    if (!name) return '';
    const stripPartnerSizeSuffix = (value) => value
      .replace(/(?:\s*(?:-|\/|,|\(|\[)?\s*(?:size\s*)?(?:XXS|XS|S|M|L|XL|XXL|2XL|3XL|4XL|5XL|OS|ONE\s*SIZE(?:\s*FITS\s*ALL)?|\d{1,2}(?:\.\d+)?)\s*[\)\]]?\s*)$/i, '')
      .trim();

    const cleaned = name.replace(/\b[A-Z0-9]{10,}\b/g, '').replace(/_/g, ' ').trim();
    return (currentUser?.preferredPartnerView !== false && product?.isPartnered)
      ? stripPartnerSizeSuffix(cleaned)
      : cleaned;
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/products/${id}`);
        if (response.data && response.data.mainProduct) {
          const main = response.data.mainProduct;
          setProduct(main);
          const allOffers = response.data.offers || [];
          setOffers(allOffers);
          
          // Initial selection: prefer the main product if it's in stock
          setActiveOffer(main);

          trackProductView({
            productId: main._id || main.id || id,
            eventType: 'pdp_view',
            feedSection: 'product_page',
          });
          trackMetaViewContent(main);
          
          const availableStock = main.retailerSizeStock?.length
            ? main.retailerSizeStock
            : (main.stock || []);
          if (availableStock.length > 0 && !isSizeInStock(main, selectedSize)) {
            const firstAvailable = availableStock.find(s => s.available === true || s.quantity > 0);
            if (firstAvailable) setSelectedSize(firstAvailable.size);
          }

          if (isPro) {
            try {
              const histRes = await api.get(`/api/products/${id}/history`);
              setPriceHistory(histRes.data);
            } catch (e) { console.error('History fetch failed'); }
          }
          
          // RE-POLL DATA: If the product was just refreshed by DeepSync on the server,
          // check back in 3 seconds to see if sizes or images have updated.
          if (main.isAffiliate) {
              console.log('STOCKROOM_DEEPSYNC: Initiating background data verification...');
              setTimeout(async () => {
                  try {
                      const refreshRes = await api.get(`/api/products/${id}`);
                      if (refreshRes.data.mainProduct.lastUpdated !== main.lastUpdated) {
                          console.log('STOCKROOM_DEEPSYNC: Market data updated successfully.');
                          setProduct(refreshRes.data.mainProduct);
                          setOffers(refreshRes.data.offers);
                      }
                  } catch (e) {}
              }, 3500);
          }
        }
      } catch (err) {
        console.error('Error loading product:', err);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchProduct();
  }, [id, isPro]);

  useEffect(() => {
    const fetchRetailerProducts = async () => {
      if (!product?.merchantName) return;
      
      try {
        setLoadingRetailerProducts(true);
        const response = await api.get('/api/products', {
          params: {
            retailers: product.merchantName,
            limit: 8,
            anchorTime: new Date().toISOString()
          }
        });
        
        // Filter out the current product
        const filtered = (response.data.products || []).filter(p => 
          String(p._id || p.id) !== String(product._id || product.id)
        );
        setRetailerProducts(filtered.slice(0, 6)); // Display max 6
      } catch (err) {
        console.error('Error loading retailer products:', err);
      } finally {
        setLoadingRetailerProducts(false);
      }
    };

    if (product) fetchRetailerProducts();
  }, [product]);

  const formatPriceLocal = (price) => {
    if (price === undefined || price === null) return '0.00';
    let numericPrice = price;
    if (typeof price === 'string') {
      numericPrice = parseFloat(price.replace(/[^0-9.]/g, ''));
    }
    if (isNaN(numericPrice)) return '0.00';
    const converted = convertPrice(numericPrice);
    return `${getCurrencySymbol()}${converted}`;
  };

  const handleOfferChange = (offer) => {
    console.log(`📡 SWITCHING_TO_OFFER: ${offer.merchantName} | ${offer.price}`);
    setActiveOffer(offer);
    
    // When switching retailers, check if the currently selected size is in stock there
    if (offer.stock && offer.stock.length > 0) {
      if (!isSizeInStock(offer, selectedSize)) {
        const firstAvailable = offer.stock.find(s => s.available === true || s.quantity > 0);
        if (firstAvailable) setSelectedSize(firstAvailable.size);
      }
    }
  };

  if (loading) {
    return (
      <div className="product-page-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#00E676" />
        <p>Loading Product Data...</p>
      </div>
    );
  }

  if (!product) return <div className="error-state">Product Not Found</div>;

  const currentDisplayProduct = activeOffer || product;
  const productId = (obj) => {
    const id = obj?._id || obj?.id;
    return id ? String(id) : null;
  };

  // AGGREGATION ENGINE: Collect unique sizes across all offers (sorted ascending)
  const allSizes = collectDisplaySizes(offers);
  
  // Use aggregated data if available, otherwise fallback
  const displaySizes = allSizes;
  const allImages = Array.from(new Set(offers.flatMap(o => o.images || [])));
  const displayImages = allImages.length > 0 ? allImages : (currentDisplayProduct.images || [currentDisplayProduct.image || '/images/default-product.jpg']);

  const discount = currentDisplayProduct.discountPercentage || currentDisplayProduct.discount || Math.round(((currentDisplayProduct.originalPrice - currentDisplayProduct.price) / currentDisplayProduct.originalPrice) * 100);
  const images = displayImages;
  const hasSizeStockEvidence = offers.some((offer) =>
    (offer.retailerSizeStock?.length || offer.stock?.length)
  );
  const isFullySoldOut = currentDisplayProduct.status === 'out_of_stock' ||
    (!currentDisplayProduct.isPartnered && hasSizeStockEvidence &&
      !offers.some((offer) => (offer.retailerSizes || offer.sizes || offer.availableSizes || [])
        .some((size) => isSizeInStock(offer, size))));

  const handleSizeClick = (size) => {
    setSelectedSize(size);
    // Best offer (lowest price) that actually has this size in stock
    const bestOffer = offers
      .filter(o => isSizeInStock(o, size))
      .sort((a, b) => a.price - b.price)[0];
    
    if (bestOffer && productId(bestOffer) !== productId(activeOffer)) {
      setActiveOffer(bestOffer);
    }
  };

  return (
    <div className="product-page-master">
      <div className="product-container">
        <div className="product-visuals">
          <button 
            className="back-link-tech" 
            onClick={() => navigate(-1)}
            style={{ 
              background: 'none', 
              border: 'none', 
              padding: 0, 
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <FontAwesomeIcon icon={faChevronLeft} /> RETURN TO MARKET
          </button>
          
          <div className="main-image-container">
            <img 
              src={images[activeImage]} 
              alt={currentDisplayProduct.name} 
              className="main-image"
            />
            {isFullySoldOut ? (
              <div className="product-page-badge sold-out-badge">
                SOLD OUT
              </div>
            ) : discount > 0 && (
              <div className="product-page-badge">
                -{discount}% OFF
              </div>
            )}
          </div>

          <div className="image-gallery">
            {images.map((img, idx) => (
              <div 
                key={idx} 
                className={`gallery-item ${activeImage === idx ? 'active' : ''}`}
                onClick={() => setActiveImage(idx)}
              >
                <img src={img} alt={`${currentDisplayProduct.name} ${idx + 1}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="product-info-panel">
          <div className="breadcrumb-tech">
            HOME {'//'} {currentDisplayProduct.category?.toUpperCase() || 'CATALOG'} {'//'} {cleanProductName(currentDisplayProduct.name).toUpperCase()}
          </div>
          
          <div className="brand-label">{currentDisplayProduct.brand?.replace(/_/g, ' ')}</div>
          <h1 className="product-title-big">{cleanProductName(currentDisplayProduct.name).toUpperCase()}</h1>

          <div className="price-section-big">
            <div className="price-main-wrap">
              <div className="price-label">CURRENT MARKET VALUE</div>
              <div className="price-3d-box">
                <span className="current">{formatPriceLocal(currentDisplayProduct.price)}</span>
              </div>
            </div>
            
            {currentDisplayProduct.originalPrice > currentDisplayProduct.price && (
              <div className="price-markdown-info">
                <div className="original-wrap">
                  <span className="msrp-label">MSRP:</span>
                  <span className="original-slashed">{formatPriceLocal(currentDisplayProduct.originalPrice)}</span>
                </div>
                <div className="discount-badge-solid">
                  {discount}% REDUCTION
                </div>
              </div>
            )}
          </div>

          {offers.length > 1 && !product.isPartnered && (
            <div className="offer-comparison-section">
               <span className="section-label">COMPARE MARKET OFFERS:</span>
               <div className="offers-grid">
                 {offers.map((offer) => {
                   const isOfferActive = productId(activeOffer) === productId(offer);
                   return (
                     <div 
                       key={productId(offer)} 
                       className={`offer-row ${isOfferActive ? 'active' : ''}`}
                       onClick={() => handleOfferChange(offer)}
                     >
                       <div className="offer-retailer">{offer.merchantName}</div>
                       <div className="offer-price">{formatPriceLocal(offer.price)}</div>
                       <div className="offer-status">{(offer.retailerSizeStock?.length
                         ? offer.retailerSizeStock
                         : (offer.stock || [])).some(s => s.quantity > 0 || s.available === true)
                         ? 'IN STOCK' : 'SOLD OUT'}</div>
                     </div>
                   );
                 })}
               </div>
            </div>
          )}

          {!currentDisplayProduct.isPartnered && displaySizes.length > 0 && (
            <div className="size-selector-wrap" style={{ marginTop: '30px' }}>
              <span className="section-label">AVAILABLE SIZES:</span>
              <div className="size-grid">
                {displaySizes.map(size => {
                  // Prefer stock quantity/available; do not treat mere presence in sizes as in-stock
                  const isAvailableSomewhere = offers.some(o => isSizeInStock(o, size));
                  
                  return (
                    <button 
                      key={size}
                      className={`size-btn ${selectedSize === size ? 'active' : ''} ${!isAvailableSomewhere ? 'sold-out' : ''}`}
                      onClick={() => isAvailableSomewhere && handleSizeClick(size)}
                      disabled={!isAvailableSomewhere}
                      title={!isAvailableSomewhere ? 'Sold Out' : ''}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="purchase-actions">
            {isFullySoldOut ? (
              <button className="add-to-cart-btn-big sold-out-btn" disabled>
                ITEM UNAVAILABLE
              </button>
            ) : (
              <button 
                onClick={async () => {
                  try {
                    await api.post('/api/products/click', {
                      productId: currentDisplayProduct._id || currentDisplayProduct.id,
                      feedSection: 'product_page',
                      sessionId: getSessionId(),
                      device: getDevice(),
                      referrer: document.referrer || 'direct',
                    });
                  } catch (e) {}
                  openExternalLink(currentDisplayProduct.affiliateUrl);
                }}
                className="add-to-cart-btn-big"
                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: 'none' }}
              >
                <FontAwesomeIcon icon={faLink} style={{ marginRight: '10px' }} />
                GET DEAL AT {currentDisplayProduct.merchantName?.toUpperCase()}
              </button>
            )}
            <button 
              className={`wishlist-btn-big ${isInWishlist(currentDisplayProduct._id || currentDisplayProduct.id) ? 'in-wishlist' : ''}`} 
              onClick={() => toggleWishlist(
                currentDisplayProduct,
                currentDisplayProduct.isPartnered ? 'ONESIZE' : selectedSize
              )}
              disabled={!currentDisplayProduct.isPartnered && !selectedSize}
              title={!currentDisplayProduct.isPartnered && !selectedSize ? 'Select an available size first' : ''}
            >
              <FontAwesomeIcon icon={faHeart} style={{ color: isInWishlist(currentDisplayProduct._id || currentDisplayProduct.id) ? '#ff4444' : 'inherit' }} />
              WATCHLIST
            </button>
          </div>

          <div className="product-details-accordion">
            <div className="details-row">
              <h4>SOURCE RETAILER</h4>
              <p>{currentDisplayProduct.merchantName}</p>
            </div>
            <div className="details-row">
              <h4>PRODUCT DESCRIPTION</h4>
              <p>{currentDisplayProduct.description || 'No description available for this Stockroom archive piece.'}</p>
            </div>

          </div>
        </div>
      </div>

      {retailerProducts.length > 0 && (
        <div className="retailer-more-products-section">
          <div className="section-header-tech">
            <span className="tech-label">COLLECTION_SYNC</span>
            <h2 className="section-title-tech">MORE FROM {product.merchantName?.toUpperCase()}</h2>
            <div className="header-line"></div>
          </div>
          
          <div className="retailer-products-grid">
            {retailerProducts.map((p) => (
              <ProductCard key={p._id || p.id} product={p} currentUser={currentUser} />
            ))}
          </div>
          
          <div className="view-all-retailer-wrap">
            <Link 
              to={`/all-brands?retailers=${encodeURIComponent(product.merchantName)}`} 
              className="view-all-retailer-btn"
            >
              EXPLORE_FULL_CATALOG
              <FontAwesomeIcon icon={faChevronRight} style={{ marginLeft: '10px' }} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductPage;
